<script setup lang="ts">
import type { ReviewCostResponse } from '@/api';
import { computed } from 'vue';

const props = defineProps<{
  preview: ReviewCostResponse | null;
  loading: boolean;
  error: string | null;
  ready: boolean;
}>();

const noticeType = computed((): 'default' | 'info' | 'warning' | 'error' => {
  if (props.error) return 'error';
  if (!props.preview) return 'default';
  if (props.preview.num_files <= 0 || props.preview.num_prompts <= 0) return 'default';
  if (props.preview.remaining_org_credits - props.preview.total_cost < 0) return 'warning';
  return 'info';
});

const remainingCreditsAfterRun = computed(() => {
  if (!props.preview) return null;
  return props.preview.remaining_org_credits - props.preview.total_cost;
});

const creditDeficit = computed(() => {
  if (remainingCreditsAfterRun.value === null || remainingCreditsAfterRun.value >= 0) {
    return 0;
  }

  return Math.abs(remainingCreditsAfterRun.value);
});

const isOverCreditLimit = computed(() => creditDeficit.value > 0);

function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value);
}
</script>

<template>
  <NAlert :type="noticeType" :show-icon="false" class="cost-preview-alert">
    <div v-if="loading" class="cost-preview-content">
      <span>Calculating cost...</span>
    </div>
    <div v-else-if="error" class="cost-preview-content">
      <strong>Cost unavailable</strong>
      <span>{{ error }}</span>
    </div>
    <div v-else-if="ready && preview" class="cost-preview-content">
      <span>
        Estimated cost:
        <strong>{{ formatCredits(preview.total_cost) }} credits</strong>
      </span>
      <span>
        {{ preview.num_files }} file{{ preview.num_files === 1 ? '' : 's' }} ×
        {{ preview.num_prompts }} prompt{{ preview.num_prompts === 1 ? '' : 's' }}
      </span>
      <span v-if="isOverCreditLimit" class="credit-warning">
        Not enough credits. You need {{ formatCredits(creditDeficit) }} more credit{{
          creditDeficit === 1 ? '' : 's'
        }}. Please contact GAIA Support.
      </span>
      <span v-else>
        Remaining after:
        <strong>{{ formatCredits(remainingCreditsAfterRun) }}</strong>
        of {{ formatCredits(preview.allowed_org_credits) }} credits
      </span>
    </div>
    <div v-else class="cost-preview-content">
      <span>Waiting for the current cost estimate...</span>
    </div>
  </NAlert>
</template>

<style scoped>
.cost-preview-alert {
  width: 100%;
}

.cost-preview-content {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  align-items: center;
  font-size: 13px;
}

.credit-warning {
  font-weight: 600;
}
</style>
